/**
 * Team Inbox — message bubbles, one per §7.2 type (Phase-1 set):
 * customer (in, white) · team (out, WA-green) · AI (violet + tag) ·
 * template caption · photo/video thumb → full-screen viewer · voice
 * player + transcript line · document card · delivery ticks
 * ◷ ✓ ✓✓ blue-✓✓ ⚠ (glyphs colour-independent — a11y §11).
 *
 * Internal notes (yellow) are Phase 2 — the `note` styling ships so a
 * server-written kind:'note' renders correctly the day it appears, but no
 * note-composing UI exists in Phase 1.
 */
import { useRef, useState } from 'react';
import { clockLabel, tickOf } from '../lib/snippets';
import { linkifyText } from '../lib/linkify';
import type { AiProposal, InboxMessage } from '../lib/types';

/**
 * Render a WhatsApp plain-text body with URLs / emails turned into real,
 * clickable anchors (shared linkifier — same one the email pane uses). Line
 * breaks are preserved. Applies to BOTH inbound and outbound text bubbles, so
 * a link a client sends from their phone (or one staff sends back) is
 * clickable in the portal. No raw-HTML injection — anchors are React nodes.
 */
function LinkifiedBody({ body }: { body: string }) {
  const lines = String(body || '').split('\n');
  return (
    <div>
      {lines.map((ln, i) => (
        <span key={i}>
          {linkifyText(ln)}
          {i < lines.length - 1 ? <br /> : null}
        </span>
      ))}
    </div>
  );
}

/**
 * Build a Google Maps URL for a location message. Prefers the structured
 * `location` field (router-threaded lat/lng/mapUrl); falls back to nothing.
 */
function locationOf(m: InboxMessage): {
  lat: number | null;
  lng: number | null;
  name: string | null;
  address: string | null;
  mapUrl: string;
} | null {
  const loc = m.location;
  if (!loc) return null;
  const lat = typeof loc.lat === 'number' ? loc.lat : null;
  const lng = typeof loc.lng === 'number' ? loc.lng : null;
  const mapUrl =
    (loc.mapUrl && /^https?:\/\//i.test(loc.mapUrl) ? loc.mapUrl : '') ||
    (lat != null && lng != null
      ? `https://www.google.com/maps?q=${lat},${lng}`
      : '');
  if (!mapUrl) return null;
  return {
    lat,
    lng,
    name: loc.name || null,
    address: loc.address || null,
    mapUrl,
  };
}

/** A 📍 location card with a clickable "View on Google Maps" link (new tab). */
function LocationCard({ m }: { m: InboxMessage }) {
  const loc = locationOf(m);
  if (!loc) {
    // Only coords-less location with no usable URL — surface the body text.
    return <LinkifiedBody body={m.body || 'Location'} />;
  }
  const title = loc.name || loc.address || 'Shared location';
  const sub =
    loc.address && loc.address !== loc.name ? loc.address : null;
  return (
    <a
      className="ti-location"
      href={loc.mapUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="ic" aria-hidden>
        📍
      </span>
      <span className="ti-location-txt">
        <span className="nm">{title}</span>
        {sub ? <span className="ad">{sub}</span> : null}
        <span className="vw">View on Google Maps ↗</span>
      </span>
    </a>
  );
}

/** Delivery tick (outbound only). */
export function Ticks({ deliveryState }: { deliveryState: string | null }) {
  const t = tickOf(deliveryState);
  return (
    <span className={`ti-tick ${t.state}`} title={t.title} aria-label={t.title}>
      {t.glyph}
    </span>
  );
}

function DocIcon({ contentType }: { contentType: string | null }) {
  const ct = String(contentType || '').toLowerCase();
  const glyph = ct.includes('pdf')
    ? '📄'
    : ct.includes('sheet') || ct.includes('excel') || ct.includes('csv')
      ? '📊'
      : '📎';
  return (
    <span className="ic" aria-hidden>
      {glyph}
    </span>
  );
}

function bubbleBody(
  m: InboxMessage,
  onOpenMedia: (m: InboxMessage) => void,
  onMediaLoad?: () => void,
) {
  switch (m.kind) {
    case 'image':
    case 'sticker':
      return (
        <>
          {m.attachment?.downloadURL ? (
            <button
              type="button"
              className="ti-media-thumb"
              onClick={() => onOpenMedia(m)}
              aria-label={`Open photo ${m.attachment.fileName || ''} full screen`}
            >
              <img
                src={m.attachment.downloadURL}
                alt={m.attachment.fileName || 'photo'}
                onLoad={onMediaLoad}
              />
            </button>
          ) : (
            <span className="ti-media-missing">📷 Photo unavailable</span>
          )}
          {m.body ? <LinkifiedBody body={m.body} /> : null}
        </>
      );
    case 'video':
      return (
        <>
          {m.attachment?.downloadURL ? (
            <button
              type="button"
              className="ti-media-thumb"
              onClick={() => onOpenMedia(m)}
              aria-label={`Play video ${m.attachment.fileName || ''}`}
            >
              <img
                src={m.attachment.downloadURL}
                alt={m.attachment.fileName || 'video'}
                onLoad={onMediaLoad}
              />
              <span className="ti-play" aria-hidden>
                ▶
              </span>
            </button>
          ) : (
            <span className="ti-media-missing">🎞 Video unavailable</span>
          )}
          {m.body ? <LinkifiedBody body={m.body} /> : null}
        </>
      );
    case 'voice':
      return (
        <>
          <span className="ti-voice">
            <span aria-hidden>🎤</span>
            {m.attachment?.downloadURL ? (
              <audio controls preload="none" src={m.attachment.downloadURL} />
            ) : (
              <span className="ti-voice-na">
                Voice note (audio stored — preview pending)
              </span>
            )}
          </span>
          {m.transcript ? (
            <div className="ti-transcript">“{m.transcript}”</div>
          ) : (
            <div className="ti-transcript">Transcribing…</div>
          )}
        </>
      );
    case 'document':
      // A dead <a href={undefined}> is a no-op click (looks broken). When the
      // URL is missing, render a plain "unavailable" card instead of a link.
      return m.attachment?.downloadURL ? (
        <a
          className="ti-doc"
          href={m.attachment.downloadURL}
          target="_blank"
          rel="noreferrer"
        >
          <DocIcon contentType={m.attachment?.contentType || null} />
          <span>
            <span className="nm">{m.attachment?.fileName || 'Document'}</span>
            <span className="ty">
              {(m.attachment?.contentType || 'file').split('/').pop()}
            </span>
          </span>
        </a>
      ) : (
        <span className="ti-media-missing">📄 Document unavailable</span>
      );
    case 'reaction':
      // AD-028 fallback bubble — the reaction's target was not in the loaded
      // thread, so we surface it as a minimal, clearly-not-a-normal-text line.
      return (
        <span className="ti-reaction-fallback">{m.body || 'Reacted'}</span>
      );
    default:
      // A WhatsApp LOCATION pin arrives as a text-kind message (router-threaded
      // body + structured `location`). Render a clickable Google Maps card.
      if (m.location || m.raw?.msgType === 'location') {
        return <LocationCard m={m} />;
      }
      // Plain text (linkified so URLs/emails a client sends become clickable).
      if (m.body) {
        return <LinkifiedBody body={m.body} />;
      }
      // No readable body. Distinguish a GENUINE Meta `unsupported`/`unknown`
      // type — where WhatsApp itself could not hand us any content (error
      // 131051: polls, payment/order messages, view-once, or a newer message
      // format) — from a merely-empty message, and explain it plainly rather
      // than showing a bare grey box.
      {
        const um = String(m.raw?.msgType || '').toLowerCase();
        if (um === 'unsupported' || um === 'unknown') {
          return (
            <em style={{ opacity: 0.65 }}>
              ⚠ WhatsApp couldn’t send us this message’s content (unsupported
              type — e.g. a poll, a payment, or a newer message format). Open in
              WhatsApp to view.
            </em>
          );
        }
        return (
          <em style={{ opacity: 0.65 }}>
            ⚠ This message has no readable text. Open in WhatsApp to view.
          </em>
        );
      }
  }
}

/** AD-028 — common quick-reaction emoji set for the portal picker. */
export const QUICK_REACTIONS = ['👍', '❤️', '🙏', '😂', '😮', '😢'];

/**
 * AD-028 — the small WhatsApp-style reaction badge(s) overlapping the bottom
 * edge of a bubble. Renders one chip per DISTINCT emoji with a count. Empty /
 * absent map → nothing.
 */
function ReactionBadges({
  reactions,
}: {
  reactions: Record<string, { emoji: string }> | null | undefined;
}) {
  if (!reactions) return null;
  const counts = new Map<string, number>();
  for (const k of Object.keys(reactions)) {
    const e = reactions[k]?.emoji;
    if (!e) continue;
    counts.set(e, (counts.get(e) || 0) + 1);
  }
  if (counts.size === 0) return null;
  return (
    <span className="ti-reactions" aria-label="Reactions">
      {[...counts.entries()].map(([emoji, n]) => (
        <span key={emoji} className="ti-reaction-badge">
          <span aria-hidden>{emoji}</span>
          {n > 1 ? <span className="ti-reaction-count">{n}</span> : null}
        </span>
      ))}
    </span>
  );
}

/**
 * AD-028 — the hover (desktop) / long-press (touch) quick-reaction picker.
 * Picking an emoji calls `onReact(emoji)`; tapping the emoji already set by
 * 'self' calls `onReact('')` (un-react). Kept minimal — no full picker.
 */
function ReactionPicker({
  open,
  selfEmoji,
  onReact,
}: {
  open: boolean;
  selfEmoji: string | null;
  onReact: (emoji: string) => void;
}) {
  if (!open) return null;
  return (
    <div className="ti-reaction-picker" role="menu" aria-label="React">
      {QUICK_REACTIONS.map((e) => (
        <button
          key={e}
          type="button"
          role="menuitem"
          className={`ti-reaction-pick${selfEmoji === e ? ' is-active' : ''}`}
          aria-label={selfEmoji === e ? `Remove ${e} reaction` : `React ${e}`}
          onClick={() => onReact(selfEmoji === e ? '' : e)}
        >
          {e}
        </button>
      ))}
    </div>
  );
}

/** M3.3 — nodeType label glyphs shown below confirmed bubbles. */
const NODE_TYPE_LABELS: Record<string, string> = {
  info: '📌 Info',
  task: '✅ Task',
  archive: '🗄 Archive',
};

/**
 * M3.3 — amber proposal chip shown below an inbound bubble when the AI
 * has classified the message but staff has not yet confirmed or dismissed.
 * Renders nothing when `aiProposal` is absent or already confirmed.
 */
export function ProposalChip({
  m,
  onConfirm,
  onDismiss,
  onCreateTask,
}: {
  m: InboxMessage;
  onConfirm: (
    nodeType: string,
    projectIds: string[],
    taskTitle: string | null,
  ) => Promise<void>;
  onDismiss: () => Promise<void>;
  onCreateTask: (title: string) => Promise<void>;
}) {
  const p: AiProposal | null | undefined = m.aiProposal;
  const [editing, setEditing] = useState(false);
  const [editType, setEditType] = useState('');
  const [editPids, setEditPids] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [busy, setBusy] = useState(false);

  if (!p || p.confirmedBy) return null;

  const handleConfirm = async (nt: string, pids: string[], title: string | null) => {
    setBusy(true);
    try {
      await onConfirm(nt, pids, title);
      if (nt === 'task' && title) await onCreateTask(title);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    setBusy(true);
    try {
      await onDismiss();
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="ti-proposal ti-proposal--edit">
        <select
          value={editType}
          onChange={(e) => setEditType(e.target.value)}
          aria-label="Node type"
        >
          <option value="info">📌 Info</option>
          <option value="task">✅ Task</option>
          <option value="archive">🗄 Archive</option>
        </select>
        {editType === 'task' && (
          <input
            className="ti-proposal-input"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder="Task title (max 8 words)"
            aria-label="Task title"
          />
        )}
        <input
          className="ti-proposal-input"
          value={editPids}
          onChange={(e) => setEditPids(e.target.value)}
          placeholder="VP numbers, comma-separated"
          aria-label="Project IDs"
        />
        <button
          type="button"
          className="ti-proposal-btn confirm"
          disabled={busy || !editType}
          onClick={() => {
            const pids = editPids
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            void handleConfirm(editType, pids, editType === 'task' ? editTitle || null : null);
            setEditing(false);
          }}
        >
          Save
        </button>
        <button
          type="button"
          className="ti-proposal-btn"
          onClick={() => setEditing(false)}
        >
          Cancel
        </button>
      </div>
    );
  }

  const label = NODE_TYPE_LABELS[p.nodeType] || p.nodeType;
  const conf = p.confidence === 'high' ? '' : ` (${p.confidence})`;

  return (
    <div className="ti-proposal">
      <span className="ti-proposal-ai-tag">AI</span>
      <span className="ti-proposal-label">{label}{conf}</span>
      {p.projectIds.length > 0 && (
        <span className="ti-proposal-pids">{p.projectIds.join(', ')}</span>
      )}
      {p.taskTitle && (
        <span className="ti-proposal-title">"{p.taskTitle}"</span>
      )}
      <button
        type="button"
        className="ti-proposal-btn confirm"
        disabled={busy}
        onClick={() =>
          void handleConfirm(p.nodeType, p.projectIds, p.taskTitle)
        }
      >
        Confirm
      </button>
      <button
        type="button"
        className="ti-proposal-btn"
        disabled={busy}
        onClick={() => {
          setEditType(p.nodeType);
          setEditPids(p.projectIds.join(', '));
          setEditTitle(p.taskTitle || '');
          setEditing(true);
        }}
      >
        Edit
      </button>
      <button
        type="button"
        className="ti-proposal-dismiss"
        disabled={busy}
        onClick={() => void handleDismiss()}
        aria-label="Dismiss AI proposal"
      >
        ✕
      </button>
    </div>
  );
}

/**
 * One message bubble. Visual class per author/type: in = white+border ·
 * out = WA-green · ai = violet + AI tag · note = yellow + Internal tag ·
 * template = green + caption.
 */
/**
 * PURE — does this bubble carry a media attachment that can be FORWARDED to
 * another chat? True for the media bubble kinds that hold a file with reachable
 * bytes (a storagePath the server can re-fetch, or a usable downloadURL).
 *
 * This is the ATTACHMENT half of forward-eligibility — it also drives the
 * text-vs-media routing (a message with a forwardable attachment forwards the
 * file; otherwise its text is forwarded). Only the proven-deliverable kinds
 * qualify: image + document round-trip cleanly (received bytes → Cloud API by
 * link). video / voice / sticker would be re-sent as a generic document
 * (untested server path), so they stay excluded — no half-working control.
 */
export function hasForwardableAttachment(m: InboxMessage): boolean {
  const a = m.attachment;
  if (!a) return false;
  if (m.kind !== 'image' && m.kind !== 'document') {
    return false;
  }
  return !!(a.storagePath || a.downloadURL);
}

/**
 * PURE — can this message be FORWARDED to another chat at all? A message is
 * forwardable when it carries REAL content: either a forwardable attachment
 * (image / document) OR a non-empty plain-text body. Everything with nothing
 * to forward is excluded so the Forward control / checkbox never appears on a
 * message that cannot go anywhere (no dead control):
 *   - system lines, internal notes and the reaction-fallback bubble (not
 *     forwardable content);
 *   - voice / video (their re-encode path is untested — excluded for now);
 *   - location pins (structured — forwarding the body would drop the map);
 *   - empty / unsupported placeholders (no body, no file).
 */
export function isForwardableMessage(m: InboxMessage): boolean {
  // Structural / non-content bubbles never forward.
  if (m.kind === 'system' || m.kind === 'note' || m.kind === 'reaction') {
    return false;
  }
  // A real file → forward the file.
  if (hasForwardableAttachment(m)) return true;
  // Voice / video stay excluded even when they carry a caption (Vera flagged
  // the re-encode path as untested — text + image + document only).
  if (m.kind === 'voice' || m.kind === 'video' || m.kind === 'sticker') {
    return false;
  }
  // A location pin renders as a map card — forwarding its body as plain text
  // would silently drop the map, so it is excluded here.
  if (m.location || m.raw?.msgType === 'location') return false;
  // Otherwise a real text body is forwardable; an empty/placeholder body is not.
  return !!(m.body && m.body.trim().length > 0);
}

export function Bubble({
  m,
  onOpenMedia,
  onMediaLoad,
  onReact,
  onForward,
  selectMode = false,
  selected = false,
  onToggleSelect,
}: {
  m: InboxMessage;
  onOpenMedia: (m: InboxMessage) => void;
  onMediaLoad?: () => void;
  /** AD-028 — react to / un-react from this message (emoji '' removes). */
  onReact?: (targetWaMessageId: string, emoji: string) => void;
  /** Forward THIS bubble to another chat/project (opens the target picker) —
   *  a file forward reuses the bytes, a text forward sends the body. Only
   *  wired for bubbles where `isForwardableMessage(m)`. */
  onForward?: (m: InboxMessage) => void;
  /** Multi-select forward — the thread is in "pick messages to forward" mode.
   *  Forwardable bubbles (text OR file) show a checkbox; the per-bubble Forward
   *  button and reactions stand down so the two controls never compete. */
  selectMode?: boolean;
  /** Whether THIS bubble is currently selected (only meaningful in selectMode). */
  selected?: boolean;
  /** Toggle THIS bubble's selection (only rendered for forwardable bubbles). */
  onToggleSelect?: (m: InboxMessage) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const canForward = isForwardableMessage(m);
  // In multi-select mode a forwardable bubble is a selectable target.
  const selectable = selectMode && canForward;
  // Label for the Forward control / checkbox — a file name for media, else a
  // short quote of the text so a screen reader announces WHAT is being forwarded.
  const forwardLabel =
    m.attachment?.fileName ||
    (m.body && m.body.trim()
      ? `“${m.body.trim().slice(0, 30)}${m.body.trim().length > 30 ? '…' : ''}”`
      : 'this message');
  const cls = [
    'ti-msg',
    m.kind === 'note' ? 'note' : m.direction === 'in' ? 'in' : m.who === 'ai' ? 'ai' : 'out',
    m.deliveryState === 'failed' ? 'failed-msg' : '',
    selectable ? 'is-selectable' : '',
    selectable && selected ? 'is-selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // AD-028 — reactions are only meaningful on a real WhatsApp message (it
  // needs a wamid to target) and never on system/note/the reaction fallback.
  // Reactions stand down while multi-select is active so the hover/long-press
  // picker never fights the selection checkbox.
  const canReact =
    !selectMode &&
    !!onReact &&
    !!m.waMessageId &&
    m.kind !== 'system' &&
    m.kind !== 'note' &&
    m.kind !== 'reaction';
  const selfEmoji = (m.reactions && m.reactions['self']?.emoji) || null;

  // Long-press (touch) opens the picker; a short timer distinguishes it from
  // a tap/scroll. Hover (desktop) is handled by mouse enter/leave.
  const lpTimer = useRef<number | null>(null);
  const startLongPress = () => {
    if (!canReact) return;
    lpTimer.current = window.setTimeout(() => setPickerOpen(true), 450);
  };
  const cancelLongPress = () => {
    if (lpTimer.current != null) {
      window.clearTimeout(lpTimer.current);
      lpTimer.current = null;
    }
  };

  const handleReact = (emoji: string) => {
    setPickerOpen(false);
    if (m.waMessageId) onReact?.(m.waMessageId, emoji);
  };

  return (
    <div
      className={cls}
      onMouseEnter={canReact ? () => setPickerOpen(true) : undefined}
      onMouseLeave={canReact ? () => setPickerOpen(false) : undefined}
      onTouchStart={canReact ? startLongPress : undefined}
      onTouchEnd={canReact ? cancelLongPress : undefined}
      onTouchMove={canReact ? cancelLongPress : undefined}
    >
      {m.crossRef && m.sourceProjectId && (
        <span className="ti-msg-tag crossref">↗ from {m.sourceProjectId}</span>
      )}
      {m.who === 'ai' && m.kind !== 'note' && (
        <span className="ti-msg-tag">AI</span>
      )}
      {m.kind === 'note' && <span className="ti-msg-tag">Internal</span>}
      {m.isEmail && <span className="ti-msg-tag email">✉ Email</span>}
      {m.kind === 'template' && m.templateName && (
        <span className="ti-msg-tag tpl">Template · {m.templateName}</span>
      )}
      {/* Multi-select — a real checkbox only on forwardable bubbles (text OR
          a file); never on system/note/voice/video (no dead control on
          nothing-to-forward). 44px label hit-area, screen-reader labelled by
          file name or a text snippet. */}
      {selectable && (
        <label className="ti-msg-select">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect?.(m)}
            aria-label={`Select ${forwardLabel} to forward`}
          />
        </label>
      )}
      <div>{bubbleBody(m, onOpenMedia, onMediaLoad)}</div>
      {/* Per-bubble single-message Forward (text OR file) stays available
          OUTSIDE select mode so both paths coexist; hidden in select mode to
          avoid two competing controls. */}
      {!selectMode && onForward && canForward && (
        <button
          type="button"
          className="ti-forward-btn"
          aria-label={`Forward ${forwardLabel} to another chat`}
          title="Forward to another chat"
          onClick={() => onForward(m)}
        >
          <span aria-hidden>↪</span> Forward
        </button>
      )}
      <span className="ti-msg-meta">
        {typeof m.at === 'number' ? clockLabel(m.at) : ''}
        {m.direction === 'out' && m.kind !== 'note' && (
          <Ticks deliveryState={m.deliveryState} />
        )}
      </span>
      {m.nodeType && (
        <span className="ti-nodetype-label">{NODE_TYPE_LABELS[m.nodeType] ?? m.nodeType}</span>
      )}
      {m.taskId && (
        <span className="ti-task-badge">💬 Chat task</span>
      )}
      {m.deliveryState === 'failed' && (
        <span className="ti-retry">⚠ Failed — retry lands with Phase 2</span>
      )}
      <ReactionBadges reactions={m.reactions} />
      {canReact && (
        <ReactionPicker open={pickerOpen} selfEmoji={selfEmoji} onReact={handleReact} />
      )}
    </div>
  );
}
